import React, { useState } from 'react';
import { CoverTemplateValues } from '../../../templates/types';
import {
  FileText,
  Building2,
  BookOpen,
  User,
  GraduationCap,
  ChevronDown,
  ChevronRight,
  Upload,
} from 'lucide-react';
import { storeAsset } from '../../../core/persistence/assetRepository';

interface CoverFieldsEditorProps {
  values: CoverTemplateValues;
  supportedFields: string[];
  onChange: (updates: Partial<CoverTemplateValues>) => void;
}

export const CoverFieldsEditor: React.FC<CoverFieldsEditorProps> = ({
  values,
  supportedFields,
  onChange,
}) => {
  const [openGroups, setOpenGroups] = useState({
    doc: true,
    institution: true,
    course: true,
    student: true,
    faculty: true,
    submission: true,
  });

  const toggleGroup = (group: keyof typeof openGroups) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev [group] }));
  };

  const isSupported = (fieldName: string) => supportedFields.includes(fieldName);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const asset = await storeAsset(file, file.name);
      onChange({ logoAssetId: asset.dataUrl });
    } catch (err) {
      console.error('Failed to upload custom logo asset:', err);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/* 1. DOCUMENT INFORMATION */}
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-surface)', overflow: 'hidden' }}>
        <div
          onClick={() => toggleGroup('doc')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--bg-surface-subtle)', cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {openGroups.doc ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            <FileText size={15} color="var(--primary-600)" />
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Document Information
            </span>
          </div>
        </div>

        {openGroups.doc && (
          <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {isSupported('documentTypeLabel') && (
              <div className="form-group">
                <label className="form-label">Document Header / Badge Label</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.documentTypeLabel}
                  onChange={(e) => onChange({ documentTypeLabel: e.target.value })}
                  placeholder="e.g. Laboratory Report, Assignment 01, Term Project"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Document Main Title *</label>
              <input
                type="text"
                className="form-input"
                style={{ fontWeight: 600 }}
                value={values.title}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder="Enter document title"
                required
              />
            </div>

            {isSupported('subtitle') && (
              <div className="form-group">
                <label className="form-label">Subtitle / Description</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={values.subtitle || ''}
                  onChange={(e) => onChange({ subtitle: e.target.value })}
                  placeholder="Enter optional subtitle or experiment scope"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2. INSTITUTION & BRANDING */}
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-surface)', overflow: 'hidden' }}>
        <div
          onClick={() => toggleGroup('institution')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--bg-surface-subtle)', cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {openGroups.institution ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            <Building2 size={15} color="var(--primary-600)" />
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Institution & Logo
            </span>
          </div>
        </div>

        {openGroups.institution && (
          <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div className="form-group">
              <label className="form-label">University Name</label>
              <input
                type="text"
                className="form-input"
                value={values.universityName}
                onChange={(e) => onChange({ universityName: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Academic Department</label>
              <input
                type="text"
                className="form-input"
                value={values.departmentName}
                onChange={(e) => onChange({ departmentName: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">University Address (Header Line)</label>
              <input
                type="text"
                className="form-input"
                value={values.universityAddress || ''}
                placeholder="A/2 Jahurul Islam Ave, Dhaka 1212"
                onChange={(e) => onChange({ universityAddress: e.target.value })}
              />
            </div>

            {/* Logo Settings */}
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={values.showLogo}
                    onChange={(e) => onChange({ showLogo: e.target.checked })}
                  />
                  <span>Display University Crest / Logo</span>
                </label>
                <label className="btn btn-ghost btn-sm text-xs" style={{ cursor: 'pointer' }}>
                  <Upload size={12} /> Replace Logo
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleLogoUpload}
                  />
                </label>
              </div>

              {values.showLogo && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginTop: 4 }}>
                  <div className="form-group">
                    <label className="form-label">Logo Alignment</label>
                    <select
                      className="form-select text-xs"
                      value={values.logoAlignment}
                      onChange={(e) => onChange({ logoAlignment: e.target.value as any })}
                    >
                      <option value="center">Centered</option>
                      <option value="left">Left Aligned</option>
                      <option value="right">Right Aligned</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Logo Size ({values.logoSizePx}px)</label>
                    <input
                      type="range"
                      min={40}
                      max={120}
                      step={4}
                      value={values.logoSizePx}
                      onChange={(e) => onChange({ logoSizePx: Number(e.target.value) })}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 3. COURSE DETAILS */}
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-surface)', overflow: 'hidden' }}>
        <div
          onClick={() => toggleGroup('course')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--bg-surface-subtle)', cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {openGroups.course ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            <BookOpen size={15} color="var(--primary-600)" />
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Course Information
            </span>
          </div>
        </div>

        {openGroups.course && (
          <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--space-3)' }}>
              <div className="form-group">
                <label className="form-label">Course Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.courseCode}
                  onChange={(e) => onChange({ courseCode: e.target.value })}
                  placeholder="e.g. CSE 207"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Course Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.courseTitle}
                  onChange={(e) => onChange({ courseTitle: e.target.value })}
                  placeholder="e.g. Data Structures"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
              <div className="form-group">
                <label className="form-label">Section / Group</label>
                <input
                  type="text"
                  className="form-input text-xs"
                  value={values.sectionOrGroup || ''}
                  onChange={(e) => onChange({ sectionOrGroup: e.target.value })}
                  placeholder="Section 04"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Semester</label>
                <input
                  type="text"
                  className="form-input text-xs"
                  value={values.semester || ''}
                  onChange={(e) => onChange({ semester: e.target.value })}
                  placeholder="Summer-2025"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Academic Year</label>
                <input
                  type="text"
                  className="form-input text-xs"
                  value={values.academicYear || ''}
                  onChange={(e) => onChange({ academicYear: e.target.value })}
                  placeholder="2025-2026"
                />
              </div>
            </div>

            {(isSupported('experimentNumber') || isSupported('groupNumber') || isSupported('experimentName')) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">Expt No</label>
                  <input
                    type="text"
                    className="form-input text-xs"
                    value={values.experimentNumber || ''}
                    onChange={(e) => onChange({ experimentNumber: e.target.value })}
                    placeholder="01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Group No</label>
                  <input
                    type="text"
                    className="form-input text-xs"
                    value={values.groupNumber || ''}
                    onChange={(e) => onChange({ groupNumber: e.target.value })}
                    placeholder="06"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expt Name</label>
                  <input
                    type="text"
                    className="form-input text-xs"
                    value={values.experimentName || ''}
                    onChange={(e) => onChange({ experimentName: e.target.value })}
                    placeholder="Diode I-V Characteristics"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 4. STUDENT DETAILS */}
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-surface)', overflow: 'hidden' }}>
        <div
          onClick={() => toggleGroup('student')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--bg-surface-subtle)', cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {openGroups.student ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            <User size={15} color="var(--primary-600)" />
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Student Information
            </span>
          </div>
        </div>

        {openGroups.student && (
          <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {isSupported('submissionDisplayMode') && (
              <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                <label className="form-label" style={{ fontWeight: 600 }}>Submission Layout Format</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${values.submissionDisplayMode === 'both' || !values.submissionDisplayMode ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '11px', padding: '4px 6px' }}
                    onClick={() => onChange({ submissionDisplayMode: 'both' })}
                  >
                    Group + Submitted By
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${values.submissionDisplayMode === 'group_only' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '11px', padding: '4px 6px' }}
                    onClick={() => onChange({ submissionDisplayMode: 'group_only' })}
                  >
                    Group Members Only
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${values.submissionDisplayMode === 'submitted_by_only' ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ fontSize: '11px', padding: '4px 6px' }}
                    onClick={() => onChange({ submissionDisplayMode: 'submitted_by_only' })}
                  >
                    Submitted By Only
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
              <div className="form-group">
                <label className="form-label">Student Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.studentName || ''}
                  placeholder="Enter Student Name (e.g. Aftab Hossain)"
                  onChange={(e) => onChange({ studentName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Student ID</label>
                <input
                  type="text"
                  className="form-input font-mono"
                  value={values.studentId || ''}
                  placeholder="e.g. 2024-1-60-001"
                  onChange={(e) => onChange({ studentId: e.target.value })}
                />
              </div>
            </div>

            {isSupported('studentEmail') && (
              <div className="form-group">
                <label className="form-label">Student Email</label>
                <input
                  type="email"
                  className="form-input text-xs"
                  value={values.studentEmail || ''}
                  placeholder="e.g. student@ewubd.edu"
                  onChange={(e) => onChange({ studentEmail: e.target.value })}
                />
              </div>
            )}

            {isSupported('groupMembers') && (values.submissionDisplayMode === 'both' || values.submissionDisplayMode === 'group_only' || !values.submissionDisplayMode) && (
              <div className="form-group">
                <label className="form-label">Group / Team Members (Name and ID per line)</label>
                <textarea
                  className="form-textarea text-xs font-mono"
                  rows={4}
                  value={values.groupMembers || ''}
                  onChange={(e) => onChange({ groupMembers: e.target.value })}
                  placeholder="Enter each member per line:&#10;Student Name 1    2023-1-60-001&#10;Student Name 2    2023-1-60-002"
                />
                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                  Tip: Separate name and ID with a space or tab. Each line becomes a table row.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 5. FACULTY & SUPERVISOR */}
      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-surface)', overflow: 'hidden' }}>
        <div
          onClick={() => toggleGroup('faculty')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3) var(--space-4)', backgroundColor: 'var(--bg-surface-subtle)', cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            {openGroups.faculty ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            <GraduationCap size={15} color="var(--primary-600)" />
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Faculty & Submission
            </span>
          </div>
        </div>

        {openGroups.faculty && (
          <div style={{ padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
              <div className="form-group">
                <label className="form-label">Instructor / Faculty Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.facultyName || ''}
                  placeholder="Enter Faculty Name (e.g. Dr. Mohammad Rashed)"
                  onChange={(e) => onChange({ facultyName: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Designation</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.facultyDesignation || ''}
                  onChange={(e) => onChange({ facultyDesignation: e.target.value })}
                  placeholder="e.g. Assistant Professor"
                />
              </div>
            </div>

            {isSupported('supervisorName') && (
              <div className="form-group">
                <label className="form-label">Project Supervisor / Advisor</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.supervisorName || ''}
                  onChange={(e) => onChange({ supervisorName: e.target.value })}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
              {isSupported('dateOfPerformance') && (
                <div className="form-group">
                  <label className="form-label">Date of Performance</label>
                  <input
                    type="text"
                    className="form-input"
                    value={values.dateOfPerformance || ''}
                    placeholder="e.g. June 30, 2025"
                    onChange={(e) => onChange({ dateOfPerformance: e.target.value })}
                  />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Date of Submission</label>
                <input
                  type="text"
                  className="form-input"
                  value={values.submissionDate}
                  placeholder="e.g. July 7, 2025"
                  onChange={(e) => onChange({ submissionDate: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
